import { StateCreator } from "zustand";
import { Store } from "../store";

type ModalState<TData> = {
  open: boolean;
  data: TData;
};

export type ModalsState = {
  agentForm: ModalState<{ partnerId: number | string | undefined }>;
  productForm: ModalState<{ productId: number | string | undefined }>;
  partnerForm: ModalState<{ partnerId: number | string | undefined }>;
  contractForm: ModalState<{
    validFrom: string | undefined;
    tariffId: number | undefined;
  }>;
  contractTable: ModalState<{ partnerId: number | string | undefined }>;
  tariffForm: ModalState<{ tariffId: number | string | undefined }>;
  tariffProductForm: ModalState<{ productId: number | string | undefined }>;
  tariffScaleForm: ModalState<{ intervalTo: number | string | undefined }>;
  tariffSpecialForm: ModalState<{
    specialCondition: number | string | undefined;
  }>;
  tariffPenaltyForm: ModalState<{
    penaltyConditionId: number | string | undefined;
    monthIntervalTo: number | string | undefined;
  }>;
  leadForm: ModalState<{ leadId: number | string | undefined }>;
  commentLeadForm: ModalState<{ leadId: number | string | undefined }>;
  leadDocument: ModalState<{ leadId: number | string | undefined }>;
  calculationForm: ModalState<{ calculationId: number | string | undefined }>;
  penaltyCalculationForm: ModalState<{
    penaltyCalculationId: number | string | undefined;
  }>;
  groupForm: ModalState<{ userId: number | string | undefined }>;
  personFrom: ModalState<{ userId: number | string | undefined }>;
};

type ModalStateChangeFns<TModalId extends keyof ModalsState> = {
  openModal: (data?: ModalsState[TModalId]["data"]) => void;
  closeModal: (data?: ModalsState[TModalId]["data"]) => void;
  changeModalData: (data: ModalsState[TModalId]["data"]) => void;
};

export type ModalsSlice = {
  modals: ModalsState;
  getModalStateChangeFns: <TModalId extends keyof ModalsState>(
    modalId: TModalId
  ) => ModalStateChangeFns<TModalId>;
};

const initialState: ModalsState = {
  agentForm: {
    open: false,
    data: { partnerId: undefined },
  },
  productForm: {
    open: false,
    data: { productId: undefined },
  },
  partnerForm: {
    open: false,
    data: { partnerId: undefined },
  },
  contractForm: {
    open: false,
    data: { tariffId: undefined, validFrom: undefined },
  },
  contractTable: {
    open: false,
    data: { partnerId: undefined },
  },
  tariffForm: {
    open: false,
    data: { tariffId: undefined },
  },
  tariffProductForm: {
    open: false,
    data: { productId: undefined },
  },
  tariffScaleForm: {
    open: false,
    data: { intervalTo: undefined },
  },
  tariffSpecialForm: {
    open: false,
    data: { specialCondition: undefined },
  },
  tariffPenaltyForm: {
    open: false,
    data: { penaltyConditionId: undefined, monthIntervalTo: undefined },
  },
  leadForm: {
    open: false,
    data: { leadId: undefined },
  },
  commentLeadForm: {
    open: false,
    data: { leadId: undefined },
  },
  leadDocument: {
    open: false,
    data: { leadId: undefined },
  },
  calculationForm: {
    open: false,
    data: { calculationId: undefined },
  },
  penaltyCalculationForm: {
    open: false,
    data: { penaltyCalculationId: undefined },
  },
  groupForm: {
    open: false,
    data: { userId: undefined },
  },
  personFrom: {
    open: false,
    data: { userId: undefined },
  },
};

export const createModalsSlice: StateCreator<Store, [], [], ModalsSlice> = (
  set
) => ({
  modals: initialState,
  getModalStateChangeFns: (modalId) => ({
    openModal: (data) =>
      set((state) => ({
        modals: {
          ...state.modals,
          [modalId]: { open: true, data: data ?? state.modals[modalId].data },
        },
      })),
    closeModal: (data) =>
      set((state) => ({
        modals: {
          ...state.modals,
          [modalId]: { open: false, data: data ?? state.modals[modalId].data },
        },
      })),
    changeModalData: (data) =>
      set((state) => ({
        modals: {
          ...state.modals,
          [modalId]: { ...state.modals[modalId], data },
        },
      })),
  }),
});
